import type {
  ScaleLinear,
  ScaleLogarithmic,
  ScaleSymLog,
  ScaleTime,
} from 'd3-scale';
import { isDate } from 'date-fns';

const OFFSET = 10;

function getOverflowPixelWithLayout(
  isPositiveInfinite: boolean,
  rangeStart: number,
  rangeEnd: number,
  layout: 'vertical' | 'horizontal',
): number {
  const isLayoutVertical = layout === 'vertical';
  let offset: number;
  if (isLayoutVertical) {
    offset = OFFSET;
  } else {
    offset = -OFFSET;
  }

  if (isPositiveInfinite) {
    return isLayoutVertical ? rangeEnd - OFFSET : rangeEnd + OFFSET;
  }
  return rangeStart + offset;
}

function getOverflowPixelNoLayout(
  isPositiveInfinite: boolean,
  isDomainReversed: boolean,
  rangeStart: number,
  rangeEnd: number,
): number {
  if (isPositiveInfinite) {
    if (isDomainReversed) {
      return rangeStart + OFFSET;
    }
    return rangeEnd - OFFSET;
  }
  if (isDomainReversed) {
    return rangeEnd - OFFSET;
  }
  return rangeStart + OFFSET;
}
function getOverflowPixel(
  isPositiveInfinite: boolean,
  isDomainReversed: boolean,
  rangeStart: number,
  rangeEnd: number,
  layout?: 'vertical' | 'horizontal',
): number {
  if (layout) {
    return getOverflowPixelWithLayout(
      isPositiveInfinite,
      rangeStart,
      rangeEnd,
      layout,
    );
  }
  return getOverflowPixelNoLayout(
    isPositiveInfinite,
    isDomainReversed,
    rangeStart,
    rangeEnd,
  );
}

/**
 * Converts a value to its corresponding pixel position on the scale. Handles finite, infinite, and date values.
 * @param value - domain value
 * @param scale - axis scale
 * @param layout - chart orientation (optional)
 * @returns The pixel position corresponding to the value on the scale.
 */
export function getThresholdPixel(
  value: number | Date,
  scale:
    | ScaleLinear<number, number>
    | ScaleLogarithmic<number, number>
    | ScaleSymLog<number, number>
    | ScaleTime<number, number>,
  layout?: 'vertical' | 'horizontal',
): number {
  if (isDate(value)) {
    return scale(value);
  }

  if (isFinite(value)) {
    return scale(value);
  }

  const [domainStart, domainEnd] = scale.domain();
  const [rangeStart, rangeEnd] = scale.range();

  const isDomainReversed = domainStart > domainEnd;
  const isPositiveInfinite = Math.sign(value) === 1;

  return getOverflowPixel(
    isPositiveInfinite,
    isDomainReversed,
    rangeStart,
    rangeEnd,
    layout,
  );
}
